import React, { ReactElement } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModelExternalCompanyData } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { WidgetBaseProps } from "models/propTypes/commonPropTypes";
import { KeyValue } from "models/utilTypes/keyValue";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { CurrencyEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  DetailsTableViewSmall,
  Icon,
  ActionLinkButton,
  useMountedState,
  ViewPanel,
  Skeletons,
  useValueFormatter,
  useCheckRelations,
} from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { useIncomingInvoiceService, useCompanyService } from "serviceHooks";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CompanyInfoWidget: React.FC<WidgetBaseProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const companyService = useCompanyService();
  const location = useLocation();
  const navigate = useNavigate();
  const { getMounted } = useMountedState();
  const { getFormattedValue, getSummaryFromExternalVatInfo } = useValueFormatter();

  const selectedCompany = useSelector((state: AppState) => state.auth.selectedCompany);
  const taxAgent = useSelector((state: AppState) => state.auth.taxAgent);

  const [companyInfo, setCompanyInfo] = React.useState<WintSuperkollAPIModelExternalCompanyData.ExternalCompanyInfo>();
  const [hasVatInvoices, setHasVatInvoices] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  const { canViewIncomingInvoices } = useIncomingInvoicePermissions();
  const { getIsAdmin } = useCheckRelations();
  const isAdmin = getIsAdmin();

  React.useEffect(() => {
    setLoading(true);
    Promise.all([checkVatInvoices(), loadCompanyInfo()]).then((res) => {
      setLoading(false);
    });
  }, [selectedCompany, taxAgent]);

  const loadCompanyInfo = () => {
    companyService
      .getExternalCompanyInformation(selectedCompany.id)
      .then((result) => {
        if (getMounted()) {
          setCompanyInfo(result);
        }
      })
      .catch((e) => {
        console.warn("error");
      });
  };

  const checkVatInvoices = () => {
    incomingInvoiceService
      .getIncomingInvoicesByTaxAgent(taxAgent)
      .then((result) => {
        setHasVatInvoices(result !== undefined ? result?.length > 0 : false);
      })
      .catch((e) => {
        console.warn("error");
      });
  };

  const onDeclarationsLinkClick = () => {
    const url = incomingInvoiceService.getIncomingInvoicesByTaxAgentUrl(taxAgent);
    navigate(UrlHelper.formatPath(url), {
      state: { background: location },
    });
  };

  const declarationsLink = React.useMemo(
    () => (
      <Grid item xs className={cssExports.dashboardActionButtonContainer}>
        <ActionLinkButton
          onClick={onDeclarationsLinkClick.bind(null)}
          endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
          className={cssExports.descriptionLink}
        >
          {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}`}
        </ActionLinkButton>
      </Grid>
    ),
    [taxAgent]
  );

  const onBoardLinkClick = () => {
    const url = "/Reports/Board";
    navigate(UrlHelper.formatPath(url), {
      state: { background: location },
    });
  };

  const getBoardLink = () => {
    return (
      <Grid item xs className={cssExports.dashboardActionButtonContainer}>
        <ActionLinkButton
          onClick={onBoardLinkClick.bind(null)}
          endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
          className={cssExports.descriptionLink}
        >
          {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.Board)}`}
        </ActionLinkButton>
      </Grid>
    );
  };

  const minifiedTableData = React.useMemo(() => {
    if (companyInfo === undefined) return [];

    const financialYear = DateHelper.formatAPIFinancialYear(companyInfo.financialYear ?? "");
    const boardVisible = isAdmin;
    const vatInvoicesVisible = canViewIncomingInvoices;

    const data: Array<KeyValue<string, string | ReactElement> | typeof ACTION_SEPARATOR | undefined> = [
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.BusinessDescription),
        companyInfo.description ?? " "
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.CurrentFinancialYear),
        DateHelper.formatDateRange(financialYear.start, financialYear.end, "-")
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.FTax),
        getFormattedValue(companyInfo.fTax)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VAT),
        getSummaryFromExternalVatInfo(companyInfo.vatInfo)
      ),
      companyInfo.vatInfo?.vat && hasVatInvoices && vatInvoicesVisible ? new KeyValue("", declarationsLink) : undefined,
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.EmployerRegistere),
        getFormattedValue(companyInfo.employerRegistered)
      ),
      boardVisible ? ACTION_SEPARATOR : undefined,
      boardVisible
        ? new KeyValue(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.Board), getBoardLink())
        : undefined,
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.ShareCapital),
        LocalizationHelperInstance.getFormattedCurrency(companyInfo.shareCapital, CurrencyEnums.CurrencyCode.SEK)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Company.NumberOfShares),
        UtilsHelper.getCountText(companyInfo.shares)
      ),
    ];
    return data;
  }, [companyInfo, selectedCompany, hasVatInvoices, canViewIncomingInvoices, isAdmin]);

  return (
    <Grid item container columnGap={2}>
      {loading ? (
        /* Todo: update with a new skeleton design for DetailsTableViewSmall,
         inside that component if the list is undefined.
         Make the skeleton story as common, so todo card redesign can use the same */
        <ViewPanel fitContent appearance="sm">
          <Skeletons.TableSkeleton />
        </ViewPanel>
      ) : (
        <DetailsTableViewSmall hideButton={true} detailsList={minifiedTableData} />
      )}
    </Grid>
  );
};

export default CompanyInfoWidget;
